<template>
  <div class="error-box" v-if="subscriptionStore.lastPaymentErrorCode">
    <img src="/public/icons/error-icon.svg" alt="" />
    <div class="txt-wrap">
      <strong>{{ t('alert_payment_failed') }}</strong>
      <p>{{ t('check_card_and_retry') }}</p>
    </div>
  </div>

  <div class="after-regist">
    <div class="badge-pst" v-if="isPaymentCard">
      <span class="badge3 success-bg">{{ t('payment_card') }}</span>
    </div>

    <div class="card-info-wrap pdt-3">
      <img src="/public/images/card.svg" alt="" />
      <p class="card-name">{{ cardInfo.cardName }}</p>
      <span class="card-num">{{ cardInfo.cardNumber }}</span>
    </div>

    <div class="flex-center rc-btn-wrap">
      <button class="gray-btn l-btn" :disabled="isLoading" @click="handleCardAdd">
        <span>{{ t('add_new_card') }}</span>
      </button>
      <button
        class="gray-btn l-btn"
        :disabled="isLoading"
        v-if="popupMode === 'MANAGE' && !isPaymentCard"
        @click="handleCardDefault"
      >
        <span>
          {{ t('set_primary_card') }}
        </span>
      </button>
      <button
        class="gray-btn l-btn"
        :disabled="isLoading"
        v-if="popupMode === 'PAYMENT' && !subscriptionStore.lastPaymentErrorCode"
        @click="emit('select-card', props.currentTab)"
      >
        <span>
          {{ t('pay_with_this_card') }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { setCardDefault } from '@/api/apiService';
import { useBrandPay } from '@/composables/domains/self-service/useBrandPay';
import { usePayment } from '@/composables/shared/usePayment';
import { useSubscriptionStore } from '@/stores/subscription';
import { CardInfo, PaymentPopupType } from '@/types/components';

const props = withDefaults(
  defineProps<{
    cardInfo: CardInfo;
    currentTab?: string;
    popupMode: PaymentPopupType;
  }>(),
  {
    popupMode: 'MANAGE',
  }
);
const emit = defineEmits(['add-card', 'complete-card-add', 'select-card']);

const { t } = useI18n();

const subscriptionStore = useSubscriptionStore();

const { addPaymentMethod, requestAgreement, isAgreedBilling, getStatusAgreement } = useBrandPay();
const { openStripeCardAddPopup } = usePayment();

const isLoading = ref<boolean>(false);

const isPaymentCard = computed(() => {
  return props.currentTab === subscriptionStore.paymentType;
});

const handleCardAdd = async () => {
  isLoading.value = true;

  switch (props.currentTab) {
    case 'TOSS':
      await addPaymentMethod(() => emit('complete-card-add'));
      break;
    case 'STRIPE':
      await openStripeCardAddPopup();
      await emit('select-card');
      break;
  }

  isLoading.value = false;
};

const handleCardDefault = async () => {
  isLoading.value = true;

  if (props.currentTab === 'TOSS') {
    await checkAgreedBilling();
  }

  await setCardDefault(props.currentTab);
  await subscriptionStore.fetchSubscriptionInfo();

  isLoading.value = false;
};

const checkAgreedBilling = async () => {
  await getStatusAgreement();

  if (!isAgreedBilling.value) {
    await new Promise<boolean>((resolve, reject) => {
      requestAgreement(
        async () => {
          resolve(true);
        },
        () => {
          reject(false);
        }
      );
    });
  }

  return true;
};
</script>

<style scoped lang="scss">
.error-box {
  margin-top: 1rem;
  background: $color-red-lighter;
  border-radius: $size-s;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: $size-s;
  img {
    position: relative;
    top: -2px;
  }
  .txt-wrap {
    strong {
      font-weight: 600;
      color: $color-red;
    }
    p {
      font-size: $font-size-psmall;
    }
  }
}

.after-regist {
  position: relative;
  margin-top: 3rem;
  .badge-pst {
    position: absolute;
    top: -22px;
    left: 50%;
    transform: translateX(-50%);
    .badge3 {
      font-size: $font-size-psmall;
    }
  }
  .card-info-wrap {
    text-align: center;
    margin-bottom: 2.5rem;
    .card-name {
      font-weight: 500;
      margin: 0.25rem 0;
    }
    .card-num {
      font-size: $font-size-psmall;
      color: $color-gray-200;
    }
  }
  .l-btn {
    text-decoration: none;
  }
  .badge3 {
    font-size: $font-size-xs;
  }
  .rc-btn-wrap {
    gap: $size-s;
    button {
      span {
        font-size: $font-size-psmall;
      }
    }
  }
}
</style>
